import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { getCurrentAccount } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, LifeBuoy, Loader2, Plus, MessageSquare, Send, Clock, Paperclip, FileText, X, Download } from 'lucide-react';
import { uploadSupportFile, downloadSupportFile, fmtSize } from '../../lib/supportAttachments';

const STATUS_STYLES = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  waiting_on_customer: 'bg-purple-100 text-purple-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-200 text-slate-600',
};
const statusLabel = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

// Reusable attachment picker + list (uploads directly to S3 on select).
function AttachmentPicker({ attachments, setAttachments, ticketId = 'new', testid }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setBusy(true);
    try {
      const out = [];
      for (const f of files) out.push(await uploadSupportFile(f, ticketId));
      setAttachments([...attachments, ...out]);
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer hover:text-slate-900" data-testid={testid}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
        <span>Attach screenshot or PDF</span>
        <input type="file" accept="image/png,image/jpeg,application/pdf" multiple className="hidden" onChange={onPick} disabled={busy} />
      </label>
      {attachments.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {attachments.map((a, i) => (
            <div key={i} className="flex items-center gap-2 border border-slate-200 px-2 py-1 text-xs bg-slate-50">
              <FileText className="h-3.5 w-3.5 text-slate-400" />
              <span className="max-w-[160px] truncate">{a.filename}</span>
              <span className="text-slate-400">{fmtSize(a.size)}</span>
              <button type="button" onClick={() => setAttachments(attachments.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AttachmentList({ attachments }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {attachments.map((a, i) => (
        <button key={i} type="button" onClick={() => downloadSupportFile(a)} className="flex items-center gap-2 border border-slate-200 px-2 py-1 text-xs bg-white hover:bg-slate-50" data-testid={`support-attachment-${a.key}`}>
          <FileText className="h-3.5 w-3.5 text-primary" />
          <span className="max-w-[160px] truncate">{a.filename}</span>
          <Download className="h-3 w-3 text-slate-400" />
        </button>
      ))}
    </div>
  );
}

export default function SupportPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('my');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ categories: [], priorities: [] });
  const [active, setActive] = useState(null); // ticket detail {ticket, updates}
  const [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [form, setForm] = useState({ subject: '', category: 'general', priority: 'normal', description: '' });
  const [newAttachments, setNewAttachments] = useState([]);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/support/tickets');
      setTickets(data.tickets || []);
    } catch (e) {
      toast.error('Failed to load your tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
    api.get('/support/meta').then(({ data }) => setMeta(data)).catch(() => {});
  }, [loadTickets]);

  const openTicket = async (id) => {
    setDetailLoading(true);
    setActive({ ticket: { id }, updates: [] });
    try {
      const { data } = await api.get(`/support/tickets/${id}`);
      setActive(data);
    } catch (e) {
      toast.error('Failed to load ticket');
      setActive(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const submitNew = async () => {
    if (form.subject.trim().length < 3) return toast.error('Please enter a subject');
    if (form.description.trim().length < 5) return toast.error('Please describe your request');
    setSubmitting(true);
    try {
      const acc = getCurrentAccount();
      await api.post('/support/tickets', { ...form, account_id: acc?.id, attachments: newAttachments });
      toast.success('Support request submitted');
      setForm({ subject: '', category: 'general', priority: 'normal', description: '' });
      setNewAttachments([]);
      await loadTickets();
      setTab('my');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() && replyAttachments.length === 0) return;
    try {
      await api.post(`/support/tickets/${active.ticket.id}/reply`, { note: reply.trim(), attachments: replyAttachments });
      setReply('');
      setReplyAttachments([]);
      await openTicket(active.ticket.id);
    } catch (e) {
      toast.error('Failed to send reply');
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="support-page">
      <header className="border-b border-border bg-card">
        <div className="px-4 md:px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" className="rounded-none" onClick={() => navigate(-1)} data-testid="support-back-button">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>Support</h1>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        {active ? (
          <TicketDetail
            data={active}
            loading={detailLoading}
            reply={reply}
            setReply={setReply}
            replyAttachments={replyAttachments}
            setReplyAttachments={setReplyAttachments}
            onSend={sendReply}
            onBack={() => { setActive(null); loadTickets(); }}
          />
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="rounded-none">
              <TabsTrigger value="my" className="rounded-none" data-testid="support-tab-my-tickets">My Tickets</TabsTrigger>
              <TabsTrigger value="new" className="rounded-none" data-testid="support-tab-new-request">New Request</TabsTrigger>
            </TabsList>

            <TabsContent value="my" className="mt-6">
              {loading ? (
                <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" />Loading…</div>
              ) : tickets.length === 0 ? (
                <div className="text-center py-20 border border-dashed border-slate-200" data-testid="support-empty">
                  <MessageSquare className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 mb-4">You haven't raised any support requests yet.</p>
                  <Button className="rounded-none" onClick={() => setTab('new')} data-testid="support-empty-new-btn"><Plus className="h-4 w-4 mr-2" />New Request</Button>
                </div>
              ) : (
                <div className="border border-slate-200 divide-y divide-slate-100" data-testid="support-tickets-list">
                  {tickets.map((t) => (
                    <button key={t.id} onClick={() => openTicket(t.id)} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center justify-between" data-testid={`support-ticket-row-${t.id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-slate-500">{t.ticket_no}</span>
                          <span className="font-semibold text-sm truncate">{t.subject}</span>
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><Clock className="h-3 w-3" />{fmt(t.created_at)}</div>
                      </div>
                      <Badge className={`rounded-none ${STATUS_STYLES[t.status] || ''}`}>{statusLabel(t.status)}</Badge>
                    </button>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="new" className="mt-6">
              <div className="max-w-2xl space-y-4 border border-slate-200 p-6" data-testid="support-new-form">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Subject</Label>
                  <Input className="rounded-none mt-1" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Brief summary of your issue" data-testid="support-input-subject" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="support-select-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(meta.categories || []).map((c) => <SelectItem key={c} value={c}>{statusLabel(c)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="support-select-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(meta.priorities || []).map((p) => <SelectItem key={p} value={p}>{statusLabel(p)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Description</Label>
                  <Textarea className="rounded-none mt-1 min-h-[140px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe what's happening, and any bill / account details that help us investigate." data-testid="support-input-description" />
                </div>
                <AttachmentPicker attachments={newAttachments} setAttachments={setNewAttachments} testid="support-new-attach" />
                <Button className="rounded-none" onClick={submitNew} disabled={submitting} data-testid="support-submit-btn">
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting…</> : <><Send className="h-4 w-4 mr-2" />Submit request</>}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

function TicketDetail({ data, loading, reply, setReply, replyAttachments, setReplyAttachments, onSend, onBack }) {
  const t = data.ticket || {};
  const updates = data.updates || [];
  return (
    <div data-testid="support-ticket-detail">
      <Button variant="ghost" size="sm" className="rounded-none mb-4" onClick={onBack} data-testid="support-detail-back"><ArrowLeft className="h-4 w-4 mr-2" />All tickets</Button>
      <div className="border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-slate-500">{t.ticket_no}</span>
              <Badge className={`rounded-none ${STATUS_STYLES[t.status] || ''}`}>{statusLabel(t.status)}</Badge>
            </div>
            <h2 className="text-lg font-bold mt-1">{t.subject}</h2>
            <div className="text-xs text-slate-400 mt-1">Raised {fmt(t.created_at)} · {statusLabel(t.category)} · {statusLabel(t.priority)} priority</div>
          </div>
        </div>
        {t.description && <p className="text-sm text-slate-700 mt-4 whitespace-pre-wrap border-t border-slate-100 pt-4">{t.description}</p>}
        <AttachmentList attachments={t.attachments} />
      </div>

      <div className="mt-6">
        <h3 className="text-xs uppercase tracking-widest text-slate-500 mb-3">Ticket history</h3>
        {loading ? (
          <div className="text-slate-400 text-sm flex items-center"><Loader2 className="h-4 w-4 animate-spin mr-2" />Loading…</div>
        ) : updates.length === 0 ? (
          <p className="text-sm text-slate-400">No responses yet. Our support team will reply here.</p>
        ) : (
          <div className="space-y-3" data-testid="support-detail-updates">
            {updates.map((u) => {
              const mine = u.author_role === 'customer';
              return (
                <div key={u.id} className={`border p-4 ${mine ? 'border-slate-200 bg-slate-50' : 'border-primary/20 bg-primary/5'}`}>
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                    <span className="font-semibold">{mine ? 'You' : (u.author_name || 'Support Team')}</span>
                    <span>{fmt(u.created_at)}</span>
                  </div>
                  {u.note && <p className="text-sm text-slate-700 whitespace-pre-wrap">{u.note}</p>}
                  <AttachmentList attachments={u.attachments} />
                  {u.status_to && <div className="text-xs text-slate-500 mt-1">Status changed to <strong>{statusLabel(u.status_to)}</strong></div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {t.status !== 'closed' && (
        <div className="mt-6 border border-slate-200 p-4">
          <Label className="text-xs uppercase tracking-widest text-slate-500">Add a reply</Label>
          <Textarea className="rounded-none mt-1 min-h-[80px]" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to support…" data-testid="support-reply-input" />
          <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
            <AttachmentPicker attachments={replyAttachments} setAttachments={setReplyAttachments} ticketId={t.id} testid="support-reply-attach" />
            <Button className="rounded-none" onClick={onSend} disabled={!reply.trim() && replyAttachments.length === 0} data-testid="support-reply-send"><Send className="h-4 w-4 mr-2" />Send reply</Button>
          </div>
        </div>
      )}
    </div>
  );
}
